/**
 * Sales, cut whichever way the question needs.
 *
 * One screen for nine reports — by item, category, waiter, cashier, table,
 * customer, channel, payment or branch — because they are the same report with
 * a different GROUP BY, and nine near-identical pages would be nine places for
 * the same bug to hide. Pick a cut; the bars rank it and the table gives the
 * detail, both against the period's total so a share reads at a glance.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { HBars } from './Charts.jsx';
import { ReportTabs } from './ReportTabs.jsx';
import { usePeriod, PeriodBar } from './period.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const DIMENSIONS = [
  ['item', 'Item'],
  ['category', 'Category'],
  ['channel', 'Order type'],
  ['payment', 'Payment'],
  ['waiter', 'Waiter'],
  ['cashier', 'Cashier'],
  ['table', 'Table'],
  ['customer', 'Customer'],
  ['branch', 'Branch'],
];

// The word for a row of each cut, for the table's first column and the empty note.
const NOUN = {
  item: 'Item',
  category: 'Category',
  channel: 'Order type',
  payment: 'Payment method',
  waiter: 'Waiter',
  cashier: 'Cashier',
  table: 'Table',
  customer: 'Customer',
  branch: 'Branch',
};

export default function BreakdownPage() {
  const period = usePeriod('day');
  const [by, setBy] = useDimension();
  // Hours within the chosen days — "2 am to 5 am" — on the order's clock time.
  const [hours, setHours] = useState({ from: '', to: '' });
  const hoursSet = Boolean(hours.from && hours.to && hours.from !== hours.to);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'breakdown', by, period.from, period.to, hoursSet ? hours.from : '', hoursSet ? hours.to : ''],
    queryFn: () =>
      api.get(
        withQuery('/reports/breakdown', {
          by,
          from: period.from,
          to: period.to,
          ...(hoursSet ? { timeFrom: hours.from, timeTo: hours.to } : {}),
        })
      ),
    placeholderData: (previous) => previous,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const hasQty = rows.some((r) => r.qty != null);

  return (
    <div className="mx-auto max-w-5xl p-4">
      <Header period={period} />

      <HoursBar hours={hours} setHours={setHours} active={hoursSet} />

      <div className="mt-3 flex flex-wrap gap-1.5">
        {DIMENSIONS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setBy(key)}
            aria-pressed={by === key}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              by === key
                ? 'border-brand-400 bg-brand-500/15 text-ink'
                : 'border-rule text-ink-2 hover:border-brand-400 hover:text-ink'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && !data ? (
        <div className="flex h-[40vh] items-center justify-center">
          <Spinner label="Loading" />
        </div>
      ) : isError ? (
        <p className="py-20 text-center text-sm text-ink-2">Could not load this report.</p>
      ) : rows.length === 0 ? (
        <p className="py-20 text-center text-sm text-ink-3">
          No sales to break down for {period.label.toLowerCase()}{hoursSet ? `, ${hours.from}–${hours.to}` : ''}.
        </p>
      ) : (
        <>
          <div className="mt-4 flex items-baseline justify-between rounded-xl border border-rule bg-surface px-4 py-3">
            <span className="text-sm text-ink-2">
              Total, {NOUN[by].toLowerCase()} by {rows.length} {rows.length === 1 ? 'row' : 'rows'}
              {hoursSet && <span className="text-ink-3"> · {hours.from}–{hours.to}</span>}
            </span>
            <span className="text-xl font-semibold tabular-nums text-ink">{formatPKR(total)}</span>
          </div>

          <section className="mt-3 rounded-xl border border-rule bg-surface p-3.5">
            <h2 className="mb-3 text-sm font-medium text-ink-2">Ranked by takings</h2>
            <HBars
              data={rows.slice(0, 12)}
              labelOf={(r) => r.label}
              valueOf={(r) => r.revenue}
              subOf={(r) => (total > 0 ? `${Math.round((r.revenue / total) * 100)}%` : '')}
              formatValue={formatPKR}
            />
          </section>

          <section className="mt-3 overflow-x-auto rounded-xl border border-rule bg-surface">
            {/* Keyed by the cut so switching dimensions remounts the table
                rather than reconciling rows of different shapes into a mix. */}
            <table key={by} className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="border-b border-rule text-left text-xs text-ink-3">
                  <th className="px-4 py-2.5 font-medium">{NOUN[by]}</th>
                  <th className="px-3 py-2.5 text-right font-medium">Orders</th>
                  {hasQty && <th className="px-3 py-2.5 text-right font-medium">Qty</th>}
                  <th className="px-3 py-2.5 text-right font-medium">Avg</th>
                  <th className="px-3 py-2.5 text-right font-medium">Share</th>
                  <th className="px-4 py-2.5 text-right font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const share = total > 0 ? (r.revenue / total) * 100 : 0;
                  const avg = r.orders > 0 ? r.revenue / r.orders : 0;
                  return (
                    <tr key={r.id ?? r.label ?? i} className="border-b border-rule/60 last:border-0">
                      <td className="px-4 py-2.5 text-ink">{r.label}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink-2">{r.orders}</td>
                      {hasQty && (
                        <td className="px-3 py-2.5 text-right tabular-nums text-ink-2">{r.qty ?? '—'}</td>
                      )}
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink-2">{formatPKR(avg)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-ink-3">{Math.round(share)}%</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums text-ink">
                        {formatPKR(r.revenue)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

// The chosen cut lives in the URL hash so a link can point straight at "by
// waiter" and a refresh keeps it.
function useDimension() {
  const initial = (typeof window !== 'undefined' && window.location.hash.replace('#', '')) || 'item';
  const [by, setByState] = useState(DIMENSIONS.some(([k]) => k === initial) ? initial : 'item');
  const setBy = (key) => {
    setByState(key);
    try {
      window.history.replaceState(null, '', `#${key}`);
    } catch {
      /* ignore */
    }
  };
  return [by, setBy];
}

/**
 * Hours of the day, on top of the days chosen: "what sold between 2 am and
 * 5 am on the 9th". Both times are needed; the same time twice, or either
 * blank, means the whole day. A window ending before it starts (22:00–02:00)
 * runs past midnight, as the shop does.
 */
function HoursBar({ hours, setHours, active }) {
  const input = 'h-9 rounded-lg border border-rule bg-surface-3 px-2 text-sm tabular-nums text-ink focus:border-brand-400 focus:outline-none';
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-ink-2">Hours</span>
      <input
        type="time"
        aria-label="From time"
        value={hours.from}
        onChange={(e) => setHours((h) => ({ ...h, from: e.target.value }))}
        className={input}
      />
      <span className="text-ink-3">to</span>
      <input
        type="time"
        aria-label="To time"
        value={hours.to}
        onChange={(e) => setHours((h) => ({ ...h, to: e.target.value }))}
        className={input}
      />
      {active ? (
        <button
          type="button"
          onClick={() => setHours({ from: '', to: '' })}
          className="rounded-full border border-rule px-3 py-1 text-xs font-medium text-ink-2 hover:border-brand-400 hover:text-ink"
        >
          All day
        </button>
      ) : (
        <span className="text-xs text-ink-3">{hours.from || hours.to ? 'Set both times' : 'All day'}</span>
      )}
    </div>
  );
}

function Header({ period }) {
  return (
    <div className="mb-4">
      <ReportTabs />
      <PeriodBar {...period} />
    </div>
  );
}
